/**
 * TelegramLinking - Component for linking Telegram account for notifications
 *
 * Features:
 * - Display linking code to send to Telegram bot
 * - Show current linking status
 * - Manage notification preferences
 * - Unlink Telegram account
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import apiService, {
  TelegramLinkCode,
  TelegramStatus,
  TelegramNotificationPreferences,
} from '../../services/api';

interface TelegramLinkingProps {
  /** Compact mode for mobile views */
  compact?: boolean;
}

const TelegramLinking: React.FC<TelegramLinkingProps> = ({ compact = false }) => {
  const [status, setStatus] = useState<TelegramStatus | null>(null);
  const [linkCode, setLinkCode] = useState<TelegramLinkCode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [updatingPrefs, setUpdatingPrefs] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  // Fetch current status
  const fetchStatus = useCallback(async () => {
    try {
      const telegramStatus = await apiService.telegram.getStatus();
      setStatus(telegramStatus);
      setError('');
    } catch (err) {
      setError('Failed to fetch Telegram status');
      console.error('Failed to fetch Telegram status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch linking code for unlinked users
  const fetchLinkCode = useCallback(async () => {
    try {
      const code = await apiService.telegram.getLinkCode();
      setLinkCode(code);
      setError('');
    } catch (err) {
      setError('Failed to generate linking code');
      console.error('Failed to fetch link code:', err);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (status && !status.is_linked) {
      fetchLinkCode();
    }
  }, [status, fetchLinkCode]);

  // Handle preference toggle
  const handlePreferenceToggle = async (
    key: keyof TelegramNotificationPreferences
  ) => {
    if (!status?.preferences) return;

    setUpdatingPrefs(true);
    setError('');

    try {
      const newValue = !status.preferences[key];
      const response = await apiService.telegram.updatePreferences({
        [key]: newValue,
      });

      setStatus((prev) =>
        prev
          ? {
              ...prev,
              preferences: response.preferences,
            }
          : null
      );
      setSuccess('Preferences updated');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update preferences');
      console.error('Failed to update preferences:', err);
    } finally {
      setUpdatingPrefs(false);
    }
  };

  // Handle unlink
  const handleUnlink = async () => {
    if (
      !window.confirm(
        'Are you sure you want to unlink your Telegram account? You will stop receiving notifications.'
      )
    ) {
      return;
    }

    setUnlinking(true);
    setError('');

    try {
      await apiService.telegram.unlink();
      setStatus({ is_linked: false, preferences: null });
      setSuccess('Telegram unlinked successfully');
      setTimeout(() => setSuccess(''), 3000);
      // Fetch new link code
      fetchLinkCode();
    } catch (err) {
      setError('Failed to unlink Telegram');
      console.error('Failed to unlink:', err);
    } finally {
      setUnlinking(false);
    }
  };

  // Copy code to clipboard
  const copyCode = async () => {
    if (!linkCode) return;

    try {
      await navigator.clipboard.writeText(linkCode.linking_code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className={`bg-gray-900 rounded-2xl p-6 ${compact ? '' : ''}`}>
        <div className="flex items-center justify-center py-8">
          <motion.div
            className="w-8 h-8 border-2 border-gray-600 border-t-white rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      </div>
    );
  }

  // Preference toggle component
  const PreferenceToggle: React.FC<{
    label: string;
    description?: string;
    preferenceKey: keyof TelegramNotificationPreferences;
    enabled: boolean;
  }> = ({ label, description, preferenceKey, enabled }) => (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1 mr-4">
        <p className="text-white text-sm font-medium">{label}</p>
        {description && (
          <p className="text-gray-500 text-xs mt-0.5">{description}</p>
        )}
      </div>
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={() => handlePreferenceToggle(preferenceKey)}
        disabled={updatingPrefs}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          enabled ? 'bg-green-500' : 'bg-gray-600'
        } ${updatingPrefs ? 'opacity-50' : ''}`}
      >
        <motion.div
          className="absolute top-1 w-4 h-4 bg-white rounded-full"
          animate={{ left: enabled ? '1.75rem' : '0.25rem' }}
          transition={{ duration: 0.2 }}
        />
      </motion.button>
    </div>
  );

  return (
    <div className="bg-gray-900 rounded-2xl p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
          <svg
            className="w-5 h-5 text-blue-400"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
          </svg>
        </div>
        <div>
          <h2 className={`font-semibold text-white ${compact ? 'text-lg' : 'text-xl'}`}>
            Telegram Notifications
          </h2>
          <p className="text-gray-500 text-sm">
            {status?.is_linked
              ? 'Your account is linked'
              : 'Link your Telegram for notifications'}
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4"
        >
          <p className="text-red-400 text-sm">{error}</p>
        </motion.div>
      )}

      {/* Success Message */}
      {success && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 mb-4"
        >
          <p className="text-green-400 text-sm">{success}</p>
        </motion.div>
      )}

      {!status?.is_linked ? (
        /* Linking Flow */
        <div>
          {linkCode && (
            <>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4">
                <p className="text-blue-400 text-sm mb-3">
                  Send this code to{' '}
                  <a
                    href="https://t.me/usegrid_bot"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-300 hover:text-blue-200 underline font-semibold"
                  >
                    @usegrid_bot
                  </a>{' '}
                  on Telegram to link your account.
                </p>
                <div className="flex items-center gap-3">
                  <code className="flex-1 bg-black/50 px-4 py-3 rounded-xl text-white font-mono text-lg text-center">
                    {linkCode.linking_code}
                  </code>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={copyCode}
                    className={`px-4 py-3 rounded-xl font-semibold transition-colors ${
                      codeCopied
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : 'bg-gray-800 text-white border border-gray-700 hover:bg-gray-700'
                    }`}
                  >
                    {codeCopied ? 'Copied!' : 'Copy'}
                  </motion.button>
                </div>
                <p className="text-gray-500 text-xs mt-2">
                  Code expires in {Math.floor(linkCode.expires_in_seconds / 60)}{' '}
                  minutes
                </p>
              </div>

              <a
                href="https://t.me/usegrid_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-3 bg-[#0088cc] hover:bg-[#0099dd] text-white font-semibold rounded-xl transition-colors text-center mb-3"
              >
                Open @usegrid_bot in Telegram
              </a>

              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={fetchLinkCode}
                className="w-full py-3 bg-gray-800 hover:bg-gray-700 text-white font-semibold rounded-xl transition-colors border border-gray-700"
              >
                Generate New Code
              </motion.button>
            </>
          )}
        </div>
      ) : (
        /* Linked State - Preferences */
        <div>
          {/* Status Badge */}
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 bg-green-500 rounded-full" />
            <span className="text-green-400 text-sm font-medium">
              Connected
            </span>
          </div>

          {/* Notification Preferences */}
          {status.preferences && (
            <div className="border-t border-gray-800 pt-4">
              <h3 className="text-white font-semibold mb-3">
                Notification Preferences
              </h3>
              <div className="divide-y divide-gray-800">
                <PreferenceToggle
                  label="Bot Status"
                  description="Get notified when your bot starts or stops"
                  preferenceKey="notify_bot_status"
                  enabled={status.preferences.notify_bot_status}
                />
                <PreferenceToggle
                  label="Errors"
                  description="Receive alerts about errors and issues"
                  preferenceKey="notify_errors"
                  enabled={status.preferences.notify_errors}
                />
                <PreferenceToggle
                  label="Milestones"
                  description="Celebrate achievements and new ATH"
                  preferenceKey="notify_milestones"
                  enabled={status.preferences.notify_milestones}
                />
                <PreferenceToggle
                  label="Weekly Summary"
                  description="Get a weekly performance summary"
                  preferenceKey="notify_weekly_summary"
                  enabled={status.preferences.notify_weekly_summary}
                />
                <PreferenceToggle
                  label="Transactions"
                  description="Notifications for deposits and withdrawals"
                  preferenceKey="notify_transactions"
                  enabled={status.preferences.notify_transactions}
                />
              </div>
            </div>
          )}

          {/* Unlink Button */}
          <div className="mt-6 pt-4 border-t border-gray-800">
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={handleUnlink}
              disabled={unlinking}
              className="w-full py-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 font-semibold rounded-xl transition-colors disabled:opacity-50"
            >
              {unlinking ? 'Unlinking...' : 'Unlink Telegram'}
            </motion.button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelegramLinking;
